// ============================================
// SUPABASE CLIENT CONFIGURATION
// ============================================

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. ' +
    'Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// ============================================
// AUTH HELPERS
// ============================================

export async function signInWithOtp(email: string) {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
    },
  });
  
  if (error) throw error;
  return { success: true };
}

export async function verifyOtp(email: string, token: string) {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: 'email',
  });
  
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

// ============================================
// EDGE FUNCTION HELPERS
// ============================================

export async function invokeEdgeFunction<T>(
  functionName: string,
  body: Record<string, unknown>
): Promise<T> {
  console.log(`Invoking edge function: ${functionName}`, body);
  
  const { data, error } = await supabase.functions.invoke(functionName, {
    body,
  });
  
  if (error) {
    console.error(`Edge function ${functionName} error:`, error);
    // Provide more specific error messages
    if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
      throw new Error('Session expired. Please refresh the page and try again.');
    }
    if (error.message?.includes('500')) {
      throw new Error('Server error. Please try again in a moment.');
    }
    if (error.message?.includes('Failed to fetch') || error.message?.includes('network')) {
      throw new Error('Network error. Please check your internet connection.');
    }
    throw error;
  }
  
  console.log(`Edge function ${functionName} response:`, data);
  return data as T;
}
